import { Button, GenericDialog } from '@/common'
import useI18n from '@/shared/hooks/useI18n'
import { useCallback } from 'react'
import { Trans } from 'react-i18next'
import { useDeleteReport } from '@/hooks/api/v2/reports'
import { useQueryClient } from '@tanstack/react-query'
import { PRODUCTION_ROUTES_QUERY_KEY } from '@/hooks/api/v2/productionRoutes'

export default function DeleteReportDialog({
  installationId,
  productionRouteId,
  reportId,
  reportPeriod,
  isOpen,
  close
}: {
  installationId: string
  productionRouteId: string
  reportId: string
  reportPeriod: string
  isOpen: boolean
  close: () => void
}) {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const { mutateAsync: deleteReport, isPending: isDeleting } = useDeleteReport(
    installationId,
    reportId
  )

  const onDeleteClick = useCallback(async () => {
    await deleteReport()
    queryClient.invalidateQueries({
      queryKey: [PRODUCTION_ROUTES_QUERY_KEY, installationId, productionRouteId]
    })
    close()
  }, [deleteReport, close, queryClient, installationId, productionRouteId])

  return (
    <GenericDialog
      size="xl"
      isOpen={isOpen}
      close={close}
      title={t('reports:deleteReportDialog.title')}
    >
      <div className="space-y-6 text-center">
        <Trans
          i18nKey="reports:deleteReportDialog.description"
          values={{ period: reportPeriod }}
          components={{ strong: <strong /> }}
        />
        <footer className="gap-2 flex justify-end">
          <Button onClick={onDeleteClick} disabled={isDeleting} variant="danger">
            {t('common:actions.delete')}
          </Button>
          <Button variant="neutral" onClick={close}>
            {t('common:actions.cancel')}
          </Button>
        </footer>
      </div>
    </GenericDialog>
  )
}
